import { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { Button, Icon, Text, tokens } from '@hotjar/hotsauce';
import FUNNELS_SPLASH_IMAGE from '@hotjar/shared-assets/images/funnels-splash.svg';
import { useSubscription } from '@hotjar/webapp/hooks/useSubscription';

const CONTAINER_WIDTH = 1256;
const TEXT_BLOCK_WIDTH = 587;
const IMAGE_WIDTH = 485;
const IMAGE_HEIGHT = 292;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap-reverse;
  justify-content: space-between;
  max-width: ${CONTAINER_WIDTH}px;
  margin: 0 auto;
  padding: 100px ${tokens.spacer.x6};
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: ${tokens.spacer.x2};
`;

const TextBlock = styled.div`
  flex: 0 0 ${TEXT_BLOCK_WIDTH}px;
  margin-right: ${tokens.spacer.x2};
`;

const Image = styled.img`
  width: ${IMAGE_WIDTH}px;
  height: ${IMAGE_HEIGHT}px;
  margin: ${tokens.spacer.x1} 0 ${tokens.spacer.x2pt5};
`;

const Subtitle = styled.div`
  display: flex;
  gap: ${tokens.spacer.x0pt5};
  margin-bottom: ${tokens.spacer.x1pt5};

  span {
    font-weight: 400;
  }
`;

const Paragraph = styled(Text)`
  margin: ${tokens.spacer.x3} 0;
`;

/* eslint-disable camelcase */
interface AdditionalTrackingData {
  ui_position: string;
  state_event_was_triggered_on?: string;
  element_name?: string;
}
/* eslint-enable camelcase */

export interface SplashScreenProps {
  trackClick(eventName: string, additionalTrackingData: AdditionalTrackingData): void;
  trackViewElement(eventName: string, additionalTrackingData: AdditionalTrackingData): void;
}

export const SplashScreen = ({ trackClick, trackViewElement }: SplashScreenProps): JSX.Element => {
  const { t } = useTranslation('funnels');
  const { upsell } = useSubscription();

  useEffect(() => {
    trackViewElement?.('Funnels_upsell_screen', {
      ui_position: 'Funnels',
    });
    // adding dependency list causing multiple `viewElement` function call
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <TextBlock>
        <Subtitle>
          <Icon icon="crown" size="m" inline />
          <Text as="p" textType="subtitle3">
            <Trans i18nKey="funnels:splashScreen.upgradeSubtitle">
              <Text as="span" textType="subtitle3">
                Upgrade to Observe Scale to unlock
              </Text>
            </Trans>
          </Text>
        </Subtitle>
        <Text as="h3" textType="header3">
          {t('splashScreen.title')}
        </Text>
        <Paragraph as="p" textType="body1">
          {t('splashScreen.text')}
        </Paragraph>
        <ButtonWrapper>
          <Button
            icon="crown"
            onClick={() => {
              trackClick?.('Funnels_upgrade_CTA', {
                ui_position: 'Funnels',
              });
              upsell('funnels_splash_screen_upgrade_link');
            }}
          >
            {t('splashScreen.upgrade')}
          </Button>
          <Button
            as="a"
            href="https://www.hotjar.com/product/funnels/"
            onClick={() => {
              trackClick?.('Funnels_learn_more_CTA', {
                ui_position: 'Funnel',
              });
            }}
            kind="secondary"
            icon="open_new_window"
            isExternal
          >
            {t('splashScreen.moreAboutFunnels')}
          </Button>
        </ButtonWrapper>
      </TextBlock>
      <Image src={FUNNELS_SPLASH_IMAGE} alt="" />
    </Container>
  );
};
